/**
 * MyFunnelWidget - Виджет "Моя воронка"
 *
 * Показывает распределение организаций по этапам воронки.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Title,
  Box,
  Loader,
  Center,
  Progress,
  Badge,
} from '@mantine/core';
import {
  IconChartDonut,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import tokens from '../../theme/designTokens';

const STAGE_CONFIG = {
  new: { label: 'Новая', color: 'gray' },
  message_sent: { label: 'Сообщение', color: 'blue' },
  call: { label: 'Звонок', color: 'cyan' },
  meeting: { label: 'Встреча', color: 'teal' },
  master_class: { label: 'Мастер-класс', color: 'green' },
  contract_signed: { label: 'Договор', color: 'lime' },
  classes_active: { label: 'Занятия', color: 'yellow' },
};

const FunnelBar = ({ stage, count, total, onClick }) => {
  const config = STAGE_CONFIG[stage] || { label: stage, color: 'gray' };
  const percentage = total > 0 ? (count / total) * 100 : 0;

  return (
    <Box
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <Group justify="space-between" mb={4}>
        <Text size="xs" fw={500}>
          {config.label}
        </Text>
        <Badge size="xs" variant="filled" color={config.color} radius="xl">
          {count}
        </Badge>
      </Group>
      <Progress
        value={percentage}
        size="md"
        color={config.color}
        radius="xl"
        styles={{
          root: {
            backgroundColor: tokens.colors.neutral[2],
          },
        }}
      />
    </Box>
  );
};

const MyFunnelWidget = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/dashboard/sales/funnel', {
        credentials: 'include',
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (err) {
      console.error('Failed to load funnel:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStageClick = (stage) => {
    navigate(`/organizations?stage=${stage}`);
  };

  // Найти максимум для масштабирования
  const maxCount = data?.stages
    ? Math.max(...Object.values(data.stages), 1)
    : 1;

  return (
    <Card shadow="sm" radius={16} withBorder p="lg" h="100%">
      <Stack gap="md">
        {/* Заголовок */}
        <Group justify="space-between">
          <Group gap="xs">
            <IconChartDonut size={20} style={{ color: tokens.colors.brand.primary }} />
            <Title order={4}>Моя воронка</Title>
          </Group>
          {data && (
            <Badge color="blue" variant="light" radius="xl" size="sm">
              {data.total}
            </Badge>
          )}
        </Group>

        {/* Воронка */}
        {loading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : data?.stages ? (
          <Stack gap="sm">
            {Object.entries(STAGE_CONFIG).map(([stage]) => {
              const count = data.stages[stage] || 0;
              return (
                <FunnelBar
                  key={stage}
                  stage={stage}
                  count={count}
                  total={maxCount}
                  onClick={() => handleStageClick(stage)}
                />
              );
            })}
          </Stack>
        ) : (
          <Center py="xl">
            <Text size="sm" c="dimmed">Нет данных</Text>
          </Center>
        )}

        {/* Дополнительная статистика */}
        {data && (data.classes_paused > 0 || data.contract_terminated > 0) && (
          <Group gap="xs" mt="auto">
            {data.classes_paused > 0 && (
              <Badge size="xs" variant="light" color="orange" radius="xl">
                На паузе: {data.classes_paused}
              </Badge>
            )}
            {data.contract_terminated > 0 && (
              <Badge size="xs" variant="light" color="red" radius="xl">
                Расторгнуто: {data.contract_terminated}
              </Badge>
            )}
          </Group>
        )}
      </Stack>
    </Card>
  );
};

export default MyFunnelWidget;
